import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import useCart from "../contexts/cartContext/cartContext";
import { useCustomer } from "../contexts/customerContext/customerContext";
import { useDiscount } from "./useDiscount";
import { usePluginspace } from "./usePluginspace";
import { useOrderMetaData } from "../contexts/orderMetaDataContext/orderMetaDataContext";
import { floatToCents } from "../functions/price_functions";
import { cpfCnpjNoMask } from "../functions/mask";
import {
  calculateCashbackEarn,
  mapProductsToCalculateItems,
} from "../pages/NewCheckoutCart/factory/cashbackHelper";
import { useCashbackFeature } from "./useCashbackFeature";

type UseCashbackEarnOptions = {
  enabled?: boolean;
  debounceMs?: number;
};

/**
 * Prévia do cashback a ganhar (acúmulo) via POST /cashback/calculate.
 * Recalcula quando produtos, cupom, frete ou cliente mudam.
 * Só chama API se a feature de cashback estiver habilitada na loja.
 */
export function useCashbackEarn(options: UseCashbackEarnOptions = {}) {
  const { enabled = true, debounceMs = 400 } = options;
  const { getToken } = usePluginspace();
  const { customer } = useCustomer();
  const { products, freightCost: cartFreightCost } = useCart();
  const { metadata } = useOrderMetaData();
  const { hasDiscount } = useDiscount();
  const { cashbackFeatureEnabled, cashbackFeatureLoaded } =
    useCashbackFeature();

  const featureEnabled =
    enabled && cashbackFeatureLoaded && cashbackFeatureEnabled;

  const [earnAmount, setEarnAmount] = useState(0);
  const [shippingCashback, setShippingCashback] = useState(0);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const customerId = Number(customer?.id || 0);
  const customerDocument = cpfCnpjNoMask(customer?.cpf || "");
  const hasCoupon = hasDiscount("coupon");
  const currentFreight = Number(metadata.freightCost || cartFreightCost || 0);

  const productsSignature = useMemo(
    () =>
      products
        .map(
          (p) =>
            `${p.id}:${p.quantity}:${p.unitPrice}:${p.promotionalPrice}:${p.sku}`,
        )
        .join("|"),
    [products],
  );

  const requestIdRef = useRef(0);

  const fetchEarn = useCallback(async () => {
    if (!featureEnabled || !customerId || products.length === 0) {
      setEarnAmount(0);
      setShippingCashback(0);
      setReady(true);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    try {
      const token = await getToken();
      const items = mapProductsToCalculateItems(products);
      const orderValue = items.reduce((sum, item) => {
        const unit = Number(item.unitPrice || item.price || 0);
        const discount = Number(item.discount || 0);
        return sum + Math.max(unit * Number(item.quantity || 0) - discount, 0);
      }, 0);

      const result = await calculateCashbackEarn(
        {
          customerId,
          customerDocument: customerDocument || undefined,
          hasCoupon,
          deliveryTax: floatToCents(currentFreight),
          orderValue,
          items,
        },
        api,
        token,
      );

      if (requestId !== requestIdRef.current) return;

      if (!result.success || !result.data) {
        setEarnAmount(0);
        setShippingCashback(0);
        setReady(true);
        return;
      }

      setEarnAmount(Number(result.data.amount || 0));
      setShippingCashback(Number(result.data.shippingCashback || 0));
      setReady(true);
    } catch (error) {
      console.error(error);
      if (requestId !== requestIdRef.current) return;
      setEarnAmount(0);
      setShippingCashback(0);
      setReady(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [
    customerDocument,
    customerId,
    currentFreight,
    featureEnabled,
    getToken,
    hasCoupon,
    products,
  ]);

  useEffect(() => {
    if (!cashbackFeatureLoaded) return;
    if (!featureEnabled) {
      setEarnAmount(0);
      setShippingCashback(0);
      setReady(true);
      setLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      void fetchEarn();
    }, debounceMs);

    return () => window.clearTimeout(timer);
  }, [
    cashbackFeatureLoaded,
    debounceMs,
    featureEnabled,
    fetchEarn,
    productsSignature,
    customerId,
    hasCoupon,
    currentFreight,
  ]);

  const shouldShowEarn = Boolean(
    featureEnabled && customerId && ready && earnAmount > 0,
  );

  return {
    shouldShowEarn,
    earnAmount,
    shippingCashback,
    loading,
    cashbackFeatureEnabled: featureEnabled,
    refreshEarn: fetchEarn,
  };
}
